/// <amd-module name="VSS/Platform/RestClientBase" />

import {
    IVssPageContext,
    IVssReadConsistencyLevel,
    IVssRestClient,
    IVssRestClientOptions,
    IVssService,
    RestErrorHandlerResponse,
    ServiceOptions,
    Services,
    VssService
} from "./Context";
import { IVssRequestOptions, issueRequest } from "./Fetch";
import { IPlatformRestErrorHandlerService } from "./IPlatformRestErrorHandler";
import { IVssTelemetryService } from "./Telemetry.types";
import { deserializeVssJsonObject } from "./Util/Serialization";
import { Uri, replaceRouteValues } from "./Util/Url";

/**
 * Parameters for sending a WebApi request
 */
export interface RestClientRequestParams {
    /**
     * Route template that is used to form the request path. If routeTemplate is NOT specified, then locationId
     * is used to lookup the template via an OPTIONS request.
     */
    routeTemplate: string;

    /**
     * The api version string to send in the request (e.g. "1.0" or "2.0-preview.2")
     */
    apiVersion: string;

    /**
     * Dictionary of route template replacement values
     */
    routeValues?: { [key: string]: any };

    /**
     * Data to post. In this case of a GET, this indicates query parameters.
     * For other requests, this is the request body object (which will be serialized
     * into a JSON string unless isRawData is set to true).
     */
    body?: any;

    /**
     * Query parameters to add to the url. In the case of a GET, query parameters can
     * be supplied via 'data' or 'queryParams'. For other verbs such as POST, the
     * data object specifies the POST body, so queryParams is needed to indicate
     * parameters to add to the query string of the url (not included in the post body).
     */
    queryParams?: { [key: string]: any };

    /**
     * HTTP verb (GET by default if not specified)
     */
    method?: string;

    /**
     * The http response (Accept) type. This is "json" (corresponds to application/json Accept header)
     * unless otherwise specified. Other possible values are "html", "text", "zip", or "binary" or their accept
     * header equivalents (e.g. application/zip).
     */
    httpResponseType?: string;

    /**
     * Allows the caller to specify custom request headers.
     */
    customHeaders?: { [headerName: string]: any };

    /**
     * If true, indicates that the raw Response should be returned in the request's resulting promise
     * rather than deserializing the response (the default).
     */
    returnRawResponse?: boolean;

    /**
     * If true, this indicates that no processing should be done on the 'data' object
     * before it is sent in the request. *This is rarely needed*. One case is when posting
     * an HTML5 File object.
     */
    isRawData?: boolean;

    /**
     * Current command for activity logging. This will override the RestClient's base option.
     */
    command?: string;

    /**
     * If true, the request will only be sent to the server if no other request
     * for the same url and parameters has been sent within the configured debounceTimeout.
     * If true and a request is already in flight within the configured debounceTimeout,
     * a promise will be returned with the results of that request.
     * Otherwise, the request will be sent to the server.
     */
    debounce?: boolean;

    /**
     * If debounce is true, this is the amount of time in milliseconds before
     * sending a new request to the server.
     * If not specified, the default is configured via a registry setting.
     */
    debounceTimeout?: number;
}

/**
 * Base class that should be used (derived from) to make requests to VSS REST apis
 */
export class RestClientBase implements IVssRestClient {
    private _options: IVssRestClientOptions;
    private _rootPath: Promise<string>;

    constructor(options: IVssRestClientOptions) {
        this._options = options || {};

        if (typeof this._options.rootPath === "string") {
            this._rootPath = Promise.resolve(this._options.rootPath);
        } else {
            this._rootPath = this._options.rootPath || Promise.resolve("/");
        }
    }

    /**
     * Gets the root path of the Service
     *
     * @returns Promise for the resolving the root path of the service.
     */
    public async getRootPath(): Promise<string> {
        return this._rootPath;
    }

    /**
     * Issue a request to a VSS REST endpoint.
     *
     * @param requestParams request options
     * @returns Promise for the response
     */
    protected async beginRequest<T>(requestParams: RestClientRequestParams): Promise<T> {
        return this._rootPath.then((rootPath: string) => {
            let requestUrl = rootPath + replaceRouteValues(requestParams.routeTemplate, requestParams.routeValues || {});
            if (requestParams.queryParams) {
                const uri = new Uri(requestUrl);
                uri.addQueryParams(requestParams.queryParams);
                requestUrl = uri.absoluteUri;
            }
            let debounceConfig: IRequestDebounceConfiguation = { debounceEnabled: false };
            let debounceService: IRestClientDebounceService | null = null;
            if (this._options.pageContext && this._options.resourceAreaId) {
                debounceService = this._options.pageContext.getService<IRestClientDebounceService>("IRestClientDebounceService");
                debounceConfig = debounceService.getDebounceConfiguration(this._options.resourceAreaId!, requestUrl, requestParams);
            }

            if (debounceConfig.debounceEnabled && debounceConfig.uniqueId && debounceService) {
                const debouncedPromise = debounceService.getDebouncedPromise<T>(debounceConfig.uniqueId);
                if (debouncedPromise) {
                    try {
                        const telemetryService = this._options.pageContext!.getService<IVssTelemetryService>("IVssTelemetryService");
                        telemetryService.publishEvent("RestClientBase", "DebouncedRequest", {
                            restAreaId: this._options.resourceAreaId,
                            url: requestUrl,
                            routeTemplate: requestParams.routeTemplate,
                            method: requestParams.method,
                            apiVersion: requestParams.apiVersion
                        });
                    } catch (error) {
                        console.warn("Error publishing debounced request telemetry", error);
                    }

                    return debouncedPromise;
                }
            }

            const response = this._issueRequest<T>(requestUrl, requestParams.apiVersion, requestParams);

            if (debounceConfig.debounceEnabled && debounceConfig.uniqueId && debounceService && debounceConfig.timeout) {
                debounceService.setDebouncedPromise<T>(debounceConfig.uniqueId, response, debounceConfig.timeout);
            }

            return response;
        });
    }

    /**
     * Issue a request to a VSS REST endpoint at the specified location
     *
     * @param requestUrl Resolved URL of the request
     * @param apiVersion API version
     * @param requestParams Optional request parameters
     */
    protected async _issueRequest<T>(requestUrl: string, apiVersion: string, requestParams: RestClientRequestParams, retryCount = 0): Promise<T> {
        const fetchOptions: RequestInit = {};

        // wait for error handling if necessary
        if (!this._options.ignoreQueue && this._options.pageContext) {
            const errorHandler = this._options.pageContext.getService<IPlatformRestErrorHandlerService>("IPlatformRestErrorHandlerService");
            await errorHandler?.requestsEnabled();
        }

        fetchOptions.method = requestParams.method || "GET";
        fetchOptions.mode = "cors";

        if (!requestParams.isRawData && requestParams.body && fetchOptions.method.toUpperCase() !== "GET") {
            fetchOptions.body = JSON.stringify(requestParams.body);
        } else {
            fetchOptions.body = requestParams.body;
        }

        const acceptType = requestParams.httpResponseType || "application/json";
        const acceptHeaderValue = `${acceptType};api-version=${apiVersion};excludeUrls=true;enumsAsNumbers=true;msDateFormat=true;noArrayWrap=true`;

        fetchOptions.headers = {
            Accept: acceptHeaderValue,
            ...(requestParams.body && { "Content-Type": "application/json" }),
            ...(this._options.readConsistencyLevel && { "X-VSS-ReadConsistencyLevel": IVssReadConsistencyLevel[this._options.readConsistencyLevel] }),
            ...requestParams.customHeaders
        };

        const vssRequestOptions = {
            authTokenProvider: this._options.authTokenProvider,
            sessionId: this._options.sessionId,
            command: requestParams.command || this._options.command
        } as IVssRequestOptions;

        let response: Response | undefined;
        let tries = 0;
        let text: string | undefined;
        while (true) {
            try {
                tries++;
                const result = issueRequest(requestUrl, fetchOptions, vssRequestOptions);
                response = await result;
                if (requestParams.returnRawResponse) {
                    return response as unknown as T;
                } else if (acceptType.toLowerCase().indexOf("json") >= 0) {
                    // MSJSON date formats must be replaced in the raw text before JSON parsing
                    text = await response.text();
                    return deserializeVssJsonObject(text)!;
                } else if (acceptType.toLowerCase() === "text/plain") {
                    return response.text() as any;
                } else {
                    return response.arrayBuffer() as unknown as T;
                }
            } catch (error) {
                const handlerResponse = await this._handleError(tries, text, response, error);
                if (handlerResponse !== RestErrorHandlerResponse.Retry) {
                    throw error;
                }
            }
        }
    }

    private async _handleError(
        tries: number,
        text: string | undefined,
        response: Response | undefined,
        error: Error
    ): Promise<RestErrorHandlerResponse> {
        const pageContext = this._options.pageContext;
        if (!pageContext || !this._options.errorHandler) {
            return RestErrorHandlerResponse.Continue;
        }
        return this._options.errorHandler(pageContext, tries, text, response, error);
    }
}

interface IRestClientDebounceService extends IVssService {
    getDebounceConfiguration(restAreaId: string, url: string, parameters: RestClientRequestParams): IRequestDebounceConfiguation;
    getDebouncedPromise<T>(uniqueId: string): Promise<T> | undefined;
    setDebouncedPromise<T>(uniqueId: string, promise: Promise<T>, timeout: number): void;
}

interface IRequestDebounceConfiguation {
    uniqueId?: string;
    debounceEnabled: boolean;
    timeout?: number;
}

interface FetchPromise {
    expiresAtTimestamp: number;
    promise: Promise<any>;
}

class RestClientDebounceService extends VssService implements IRestClientDebounceService {
    private debouncedFetchPromises: Map<string, FetchPromise> = new Map<string, FetchPromise>();
    private endpointConfigurations: Map<string, DebounceAPIEndpointConfiguration> = new Map<string, DebounceAPIEndpointConfiguration>();
    private debounceEnabled: boolean = false;
    private defaultDebounceTimeout: number = 100;

    getDebouncedPromise<T>(uniqueId: string): Promise<T> | undefined {
        const debouncedPromise = this.debouncedFetchPromises.get(uniqueId);
        if (debouncedPromise) {
            if (debouncedPromise.expiresAtTimestamp > Date.now()) {
                return debouncedPromise.promise as Promise<T>;
            } else {
                this.debouncedFetchPromises.delete(uniqueId);
            }
        }
        return undefined;
    }

    setDebouncedPromise<T>(uniqueId: string, promise: Promise<T>, timeout: number): void {
        this.debouncedFetchPromises.set(uniqueId, { promise, expiresAtTimestamp: Date.now() + timeout });
        setTimeout(() => {
            this.debouncedFetchPromises.delete(uniqueId);
        }, timeout * 2);
    }

    override _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        // Can't directly reference the IVssContributionService here without causing a circular dependency
        // This is safe though because calling getData does not cause a circular dependency
        const contributionService = pageContext.getService("IVssContributionService");
        const debounceConfig = (contributionService as any).getData("ms.vss-web.debounce-api-calls-data") as DebounceApiCallsData;
        if (debounceConfig) {
            this.debounceEnabled = debounceConfig.enabled;
            this.defaultDebounceTimeout = debounceConfig.defaultTimeout ? debounceConfig.defaultTimeout : this.defaultDebounceTimeout;
            debounceConfig.endpointConfigurations.forEach(endpointConfiguration => {
                this.endpointConfigurations.set(
                    `${endpointConfiguration.areaId}|${endpointConfiguration.apiVersion}|${endpointConfiguration.routeTemplate}|${endpointConfiguration.httpMethod}`,
                    endpointConfiguration
                );
            });
        }
    }

    public getDebounceConfiguration<T>(restAreaId: string, url: string, parameters: RestClientRequestParams): IRequestDebounceConfiguation {
        if (this.debounceEnabled && !parameters.returnRawResponse) {
            // Raw responses are not supported for debounce because response.text() can only be called once on a response.
            // If a raw response is returned, we don't have any control over when response.text() is called
            if ((parameters.method?.toUpperCase() ?? "GET") === "GET" && parameters.debounce !== false) {
                const endpointConfiguration = this.getEndpointConfiguration(restAreaId, parameters);
                if (endpointConfiguration) {
                    // Configuration has been overridden for this endpoint. Use values from the configuration instead of values passed in
                    return {
                        debounceEnabled: endpointConfiguration.debounce,
                        uniqueId: this.getUniqueIdForRequest(restAreaId, url, parameters),
                        timeout: endpointConfiguration.debounceTimeout ?? parameters.debounceTimeout ?? this.defaultDebounceTimeout
                    };
                }
                return {
                    debounceEnabled: true,
                    uniqueId: this.getUniqueIdForRequest(restAreaId, url, parameters),
                    timeout: parameters.debounceTimeout ?? this.defaultDebounceTimeout
                };
            }
        }
        return { debounceEnabled: false };
    }

    private getEndpointConfiguration(areaId: string, param: RestClientRequestParams): DebounceAPIEndpointConfiguration | undefined {
        return this.endpointConfigurations.get(`${areaId}|${param.apiVersion}|${param.routeTemplate}|${param.method?.toUpperCase() ?? "GET"}`);
    }

    private getUniqueIdForRequest(restAreaId: string, url: string, parameters: RestClientRequestParams): string {
        return `${restAreaId}|${parameters.apiVersion}|${url}|${parameters.method ?? "GET"}}`;
    }
}

interface DebounceApiCallsData {
    enabled: boolean;
    defaultTimeout: number;
    endpointConfigurations: DebounceAPIEndpointConfiguration[];
}

interface DebounceAPIEndpointConfiguration {
    areaId: string;
    apiVersion: string;
    routeTemplate: string;
    httpMethod: string;
    debounce: boolean;
    debounceTimeout?: number;
}

Services.add("IRestClientDebounceService", { serviceFactory: RestClientDebounceService, options: ServiceOptions.Startup });
